import { IGetLatestPaymentInfoResponse } from '@/shared/api/billing/types';
import { LS_UTM_PARAMS } from '@/shared/constants/localStorage';
import { User } from '@/shared/types/User';

import { IS_GTM_DEBUGGER_ENABLED } from './config';
import { BeginCheckoutEventData, RegistrationSurveyEventData, ViewItemListEventData } from './types';

window.dataLayer = window.dataLayer || [];

export const pushEvent = ({ eventName, properties }: { eventName: string; properties?: object }) => {
  window.dataLayer.push({
    event: eventName,
    ...properties,
  });
  if (IS_GTM_DEBUGGER_ENABLED) {
    console.info('%cGTM debugger event:', 'color: #F79009');
    console.info({ eventName, ...properties });
  }
};

export const pushVariables = (variables: { [key: string]: string | undefined }) => {
  window.dataLayer.push({
    ...variables,
  });
  if (IS_GTM_DEBUGGER_ENABLED) {
    console.info('%cGTM debugger set variables:', 'color: #F79009');
    console.info({ ...variables });
  }
};

const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid'] as const;

/**
 * Read UTM/gclid from current URL and push to dataLayer for GA4 custom dimensions.
 * Falls back to localStorage when URL has no attribution params (e.g. after OAuth redirect).
 * Called once on app init.
 */
export const pushAttributionToDataLayer = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = urlParams.get(key);
    if (value) attribution[key] = value;
  }

  // Fallback: after OAuth redirect the URL is clean, read stored params
  if (Object.keys(attribution).length === 0) {
    try {
      const stored = window.localStorage.getItem(LS_UTM_PARAMS);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string>;
        for (const key of ATTRIBUTION_KEYS) {
          if (parsed[key]) attribution[key] = parsed[key];
        }
      }
    } catch {
      // localStorage unavailable or corrupted — skip silently
    }
  }

  if (Object.keys(attribution).length > 0) {
    pushVariables(attribution);
  }
};

export const GTMEvents = {
  beginCheckout: (eventData: BeginCheckoutEventData) => {
    pushEvent({ eventName: 'begin_checkout', properties: eventData });
  },

  customerSurvey: (eventData: RegistrationSurveyEventData) => {
    pushEvent({ eventName: 'customerSurvey', properties: eventData });
  },

  login: () => {
    pushEvent({ eventName: 'login' });
  },

  purchase: (latestPaymentInfo: IGetLatestPaymentInfoResponse) => {
    pushEvent({ eventName: 'purchase', properties: { ecommerce: latestPaymentInfo } });
  },

  registration: () => {
    pushEvent({ eventName: 'registration' });
  },

  signUpPageShown: () => {
    pushEvent({ eventName: 'signUpPageShown' });
  },

  subscriptionPageOpened: () => {
    pushEvent({ eventName: 'subscriptionPageOpened' });
  },

  videoUpload: () => {
    pushEvent({ eventName: 'videoUpload' });
  },

  viewItemList: (eventData: ViewItemListEventData) => {
    pushEvent({ eventName: 'view_item_list', properties: eventData });
  },

  setUserId: (userId?: User['id']) => {
    pushVariables({ user_id: userId });
  },
};
